import {addLanguage} from '../../redux/reducers/languageSlice'
import {useDispatch} from 'react-redux'
import {addCohort} from '../../redux/reducers/cohortSlice'
import {addUnit} from '../../redux/reducers/unitSlice'
import {useNavigate} from 'react-router-dom'
import axios from 'axios'
import api from '../../../_metronic/helpers/api'
import {showAlert} from '../../redux/reducers/alertSlice'
import {stringify} from 'qs'
import {addCategory} from '../../redux/reducers/categorySlice'
import {addModule} from '../../redux/reducers/moduleSlice'
import {addSeason} from '../../redux/reducers/seasonSlice'

function useUtils() {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const fetchCourses = async (
    text = null,
    status = null,
    cohortId = null,
    categoryId = null,
    seasonId = null,
    limit = 10,
    offset = 0
  ) => {
    const params = {
      limit,
      offset,
      relations: ['cohort', 'category', 'language'],
      where: {},
    }

    if (text) {
      params.text = text
    }

    if (status && status !== 'Status') {
      params.where.status = status
    }

    if (cohortId && cohortId !== 'Cohort') {
      params.where.cohortId = cohortId
    }

    if (categoryId && categoryId !== 'Category') {
      params.where.categoryId = categoryId
    }

    if (seasonId && seasonId !== 'Season') {
      params.where.seasonId = seasonId
    }

    try {
      const response = await api.get(`/course?${stringify(params)}`)
      if (response.data.status) {
        return response.data.data
      }
    } catch (error) {
      dispatch(
        showAlert({
          message: 'Network error',
          variant: 'danger',
        })
      )
    }
  }

  const createCourse = async (values, bannerMediaId, squareMediaId) => {
    try {
      let data = {
        code: values.code,
        title: values.title,
        authorName: values.authorName,
        cohortId: values.cohortId,
        languageId: values.languageId,
        categoryId: values.categoryId,
        bannerMediaId,
        squareMediaId,
        shortDescription: values.shortDescription,
        longDescription: values.longDescription,
        tags: values.tags,
        moduleIds: values.moduleIds,
        learningPoints: values.learningPoints,
        isFeatured: values.isFeatured,
        isForBlackforceMember: values.isForBlackforceMember, //
        seasonId: values.seasonId, //
      }
      const response = await api.post(`/course`, data)
      if (values.isClone) {
          dispatch(showAlert({message: 'Course Cloned', variant: 'success'}))
          navigate('/courselist')
      } else {
        if (response?.data?.status) {
          values = {...values, id: response?.data?.data.id, status: null}
          dispatch(showAlert({message: 'Course Created', variant: 'success'}))
          navigate('/courselist/preview-course', {state: {values}})
        }
      }
    } catch (error) {
      dispatch(showAlert({message: error?.response?.data?.message, variant: 'danger'}))
    }
  }

  const uploadImage = async (values) => {
    if (values.bannerMediaId && values.squareMediaId) {
      createCourse(values, values.bannerMediaId, values.squareMediaId)
    } else {
      try {
        let bannerMediaId = values.bannerMediaId
        let squareMediaId = values.squareMediaId

        // Upload banner image if it's new
        if (values.bannerImage && !values.bannerMediaId) {
          const uniqueFileName = `banner_${Date.now()}`
          const imageFile = values.bannerImage
          const fileType = imageFile.type
          const fileSize = `${imageFile.size}`

          const uploadDetails = {
            fileName: uniqueFileName,
            fileType: fileType,
            size: fileSize,
          }

          const uploadResponse = await api.post('s3/upload-url', uploadDetails)
          const {mediaId, signedUrl} = uploadResponse.data.data

          const imageUploadResponse = await axios.put(signedUrl, imageFile, {
            headers: {
              'Content-Type': fileType,
            },
          })
          if (imageUploadResponse.status !== 200) {
            throw new Error('Banner image upload failed')
          }
          bannerMediaId = mediaId
        }

        // Upload square image if it's new
        if (values.squareImage && !values.squareMediaId) {
          const uniqueFileName = `square_${Date.now()}`
          const imageFile = values.squareImage
          const fileType = imageFile.type
          const fileSize = `${imageFile.size}`

          const uploadDetails = {
            fileName: uniqueFileName,
            fileType: fileType,
            size: fileSize,
          }

          const uploadResponse = await api.post('s3/upload-url', uploadDetails)
          const {mediaId, signedUrl} = uploadResponse.data.data

          const imageUploadResponse = await axios.put(signedUrl, imageFile, {
            headers: {
              'Content-Type': fileType,
            },
          })
          if (imageUploadResponse.status !== 200) {
            throw new Error('Square image upload failed')
          }
          squareMediaId = mediaId
        }

        createCourse(values, bannerMediaId, squareMediaId)
      } catch (error) {
        console.log('Error uploading images:', error, error.message)
      }
    }
  }

  const publishCourse = async (id) => {
    try {
      const response = await api.patch(`course/${id}`, {status: 'published'})
      console.log(response)
      if (response.data.status) {
        dispatch(showAlert({message: 'Course Published', variant: 'success'}))
        navigate('/courselist')
      }
    } catch (error) {
      dispatch(showAlert({message: error?.response?.data?.message, variant: 'danger'}))
    }
  }

  const handleEdit = async (id) => {
    const params = {
      relations: ['cohort', 'category', 'language', 'courseModule.module', 'learningPoint', 'tag'],
    }

    try {
      const response = await api.get(`/course/${id}?${stringify(params)}`)
      if (response.data.status) {
        return response.data.data
      }
    } catch (error) {
      dispatch(
        showAlert({
          message: 'Network error',
          variant: 'danger',
        })
      )
    }
  }

  const fetchCategories = async () => {
    try {
      const response = await api.get(
        `/category?select[id]=true&select[name]=true&select[createdAt]=true?&order[name]=DESC`,
        {
          params: {
            returnAllRecords: true,
          },
        }
      )
      if (response.data.status) {
        dispatch(addCategory(response.data.data))
      }
    } catch (error) {}
  }

  const fetchLanguages = async () => {
    try {
      const response = await api.get(`/language`, {
        params: {
          returnAllRecords: true,
        },
      })
      if (response.data.status) {
        dispatch(addLanguage(response.data.data))
      }
    } catch (error) {
      dispatch(
        showAlert({
          message: error?.response?.data?.message,
          variant: 'danger',
        })
      )
    }
  }

  const fetchCohorts = async () => {
    try {
      const response = await api.get(
        `/cohort?select[id]=true&select[title]=true&select[createdAt]=true&order[title]=ASC`,
        {
          params: {
            returnAllRecords: true,
          },
        }
      )
      if (response.data.status) {
        dispatch(addCohort(response.data.data))
      }
    } catch (error) {}
  }

  const fetchModules = async (languageId) => {
    const params = {
      returnAllRecords: true,
      where: {},
    }
    
    if (languageId ) {
      params.where.languageId = languageId
    }
    try {
      const response = await api.get(`/module?${stringify(params)}`)
      if (response.data.status) {
        dispatch(addModule(response.data.data))
      }
    } catch (error) {
      dispatch(showAlert({message: error?.response?.data?.message, variant: 'danger'}))
    }
  }

  const deleteCourse = async (id, limit, offset) => {
    try {
      const response = await api.delete(`/course/${id}`)
      if (response?.data?.status) {
        dispatch(showAlert({message: 'Course deleted', variant: 'success'}))
        return fetchCourses(null, null, null, null, null, limit, offset)
      }
    } catch (error) {
      dispatch(showAlert({message: error?.response?.data?.message, variant: 'danger'}))
    }
  }

  const fetchAllCourses = async (text = null,
    status = null,
    cohortId = null,
    categoryId = null,
    seasonId = null,) => {
    const params = {
      limit: 100,
      offset: 0,
      order: {createdAt: 'DESC'},
      relations: ['courseModule', 'learningPoint', 'tag'],
      where: {},
    }

    if (text) {
      params.text = text
    }

    if (status && status !== 'Status') {
      params.where.status = status
    }

    if (cohortId && cohortId !== 'Cohort') {
      params.where.cohortId = cohortId
    }

    if (categoryId && categoryId !== 'Category') {
      params.where.categoryId = categoryId
    }

    if (seasonId && seasonId !== 'Season') {
      params.where.seasonId = seasonId
    }

    let allData = []
    let totalRecords = 0
    try {
      const initialResponse = await api.get(`/course?${stringify(params)}`)

      if (initialResponse.data.status) {
        totalRecords = initialResponse.data.data.count
        allData = initialResponse.data.data.records

        const totalChunks = Math.ceil(totalRecords / params.limit)
        const promises = []

        for (let i = 1; i < totalChunks; i++) {
          params.offset = i * params.limit
          promises.push(api.get(`/course?${stringify(params)}`))
        }

        const responses = await Promise.all(promises)
        responses.forEach((response) => {
          if (response.data.status) {
            allData = [...allData, ...response.data.data.records]
          }
        })

        if (allData?.length > 0) {
          return allData?.map((course) => ({
            id: course?.id,
            code: course?.code || '',
            title: course?.title || '',
            authorName: course?.authorName || '',
            cohort: course?.cohortId || null,
            language: course?.languageId || null,
            category: course?.categoryId || null,
            isFeatured: course?.isFeatured || false,
            season: course?.seasonId,
            ForBlackforceMember: course?.isForBlackforceMember || false,
            shortDescription: course?.shortDescription || '',
            longDescription: course?.longDescription || '',
            modules: course?.courseModule.map((module) => module.moduleId).filter(Boolean),
            tags: course?.tag ? course?.tag.map((tag) => tag.name) : [],
            learningPoints: course?.learningPoint
              ? course?.learningPoint.map((point) => point.name)
              : [],
          }))
        }
      }
    } catch (error) {
      dispatch(
        showAlert({
          message: error?.response?.data?.message || 'Something went wrong',
          variant: 'danger',
        })
      )
    }
  }
  const addCSV = async (data) => {
    try {
      const response = await api.post(`/course/add-bulk`, data)
      if (response?.data?.status) {
        return response?.data?.data
      }
    } catch (error) {
      dispatch(showAlert({message: error?.response?.data?.message, variant: 'danger'}))
    }
  }

  const fetchSeasons = async () => {
    const params = {
      returnAllRecords: true,
      offset: 0,
    }

    try {
      const response = await api.get(
        `/season?${stringify(params)}&select[id]=true&select[name]=true&order[name]=ASC`
      )
      if (response.data.status) {
        dispatch(addSeason(response.data.data))
      }
    } catch (error) {
      console.log(error?.message)
    }
  }

  const submitReport = async (data) => {
    try {
      const response = await api.post(`/setting/sendCSVReport`, data)
      return response
    } catch (error) {}
  }

  return {
    addCSV,
    fetchCategories,
    fetchCohorts,
    fetchLanguages,
    fetchCourses,
    fetchModules,
    publishCourse,
    uploadImage,
    handleEdit,
    deleteCourse,
    fetchAllCourses,
    fetchSeasons,
    submitReport,
  }
}

export default useUtils
